import { createSeededRandom } from '../utils/random';

/** Far-off sounds somewhere in the neighbourhood, heard through the night air. */
export type DistantSound = 'chime' | 'howl' | 'clang' | 'slam' | 'cry';

export type AudioFrame = {
  tension: number;
  active: boolean;
  /** Breathing intensity 0..1 (exhaustion / panic). */
  breath: number;
  /** 0..1 how close the nearest vending machine is. */
  hum: number;
};

/**
 * Fully procedural Web Audio soundscape: no asset files. Gameplay code calls
 * the event methods; `update` drives the continuous layers.
 */
export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private ambienceGain: GainNode | null = null;
  private droneGain: GainNode | null = null;
  private droneFilter: BiquadFilterNode | null = null;
  private humGain: GainNode | null = null;
  private rainGain: GainNode | null = null;
  /** Long, dark tail for distant sounds and his footsteps (send into it). */
  private reverbIn: GainNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private tinnitusGain: GainNode | null = null;
  /** 0..1 how windy this night is; gusts come and go on top. */
  private windBase = 0.25;
  private gustStart = -1;
  private gustLength = 0;
  private nextGust = 12;
  /** 0..1 the current gust (shakes the bamboo). */
  gust = 0;
  private nextBeat = 0;
  private nextCricket = 0;
  /** Crickets only sing at night. */
  private crickets = true;
  /** 0..1 rain drumming on her open umbrella. */
  private umbrellaRain = 0;
  /** Footsteps crunch on snow instead of clicking on asphalt. */
  private snow = false;
  private nextBreath = 0;
  private nextTrain = 30;
  private clock = 0;
  private readonly rng = createSeededRandom(4242);
  private muted = false;
  /** Shaping curves for the far-off sounds' distortion (built on first use). */
  private clipCurve: Float32Array<ArrayBuffer> | null = null;

  async unlock(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state !== 'running') await this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    this.ctx = ctx;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 3;
    comp.connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(comp);

    const len = ctx.sampleRate * 2;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1;

    // Distant city rumble
    const amb = ctx.createBufferSource();
    amb.buffer = this.noise;
    amb.loop = true;
    const ambLp = ctx.createBiquadFilter();
    ambLp.type = 'lowpass';
    ambLp.frequency.value = 260;
    this.ambienceGain = ctx.createGain();
    this.ambienceGain.gain.value = 0.11;
    amb.connect(ambLp).connect(this.ambienceGain).connect(this.master);
    amb.start();

    // Dread drone: two detuned saws, opened up by tension.
    this.droneFilter = ctx.createBiquadFilter();
    this.droneFilter.type = 'lowpass';
    this.droneFilter.frequency.value = 180;
    this.droneGain = ctx.createGain();
    this.droneGain.gain.value = 0;
    for (const f of [55, 55.6, 82.4]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.connect(this.droneFilter);
      o.start();
    }
    this.droneFilter.connect(this.droneGain).connect(this.master);

    // Vending machine hum
    this.humGain = ctx.createGain();
    this.humGain.gain.value = 0;
    for (const f of [100, 200.5]) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(this.humGain);
      o.start();
    }
    this.humGain.connect(this.master);

    // Rain: bright noise, a little lowpassed.
    const rain = ctx.createBufferSource();
    rain.buffer = this.noise;
    rain.loop = true;
    const rainHp = ctx.createBiquadFilter();
    rainHp.type = 'highpass';
    rainHp.frequency.value = 900;
    const rainLp = ctx.createBiquadFilter();
    rainLp.type = 'lowpass';
    rainLp.frequency.value = 7000;
    this.rainGain = ctx.createGain();
    this.rainGain.gain.value = 0;
    rain.connect(rainHp).connect(rainLp).connect(this.rainGain).connect(this.master);
    rain.start();

    // Night air: a long tail that makes far-off sounds (and his steps) hang between the houses.
    const reverb = ctx.createConvolver();
    reverb.buffer = this.impulse(ctx, 3.2);
    const damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2400;
    const wet = ctx.createGain();
    wet.gain.value = 0.6;
    this.reverbIn = ctx.createGain();
    this.reverbIn.connect(reverb).connect(damp).connect(wet).connect(this.master);

    // Wind: hollow band-passed noise that rises and falls; gusts on top.
    const wind = ctx.createBufferSource();
    wind.buffer = this.noise;
    wind.loop = true;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 380;
    this.windFilter.Q.value = 0.9;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    wind.connect(this.windFilter).connect(this.windGain).connect(this.master);
    wind.start(0, 0.7);

    // Ringing in her ears when he's right behind her.
    const ring = ctx.createOscillator();
    ring.frequency.value = 7350;
    this.tinnitusGain = ctx.createGain();
    this.tinnitusGain.gain.value = 0;
    ring.connect(this.tinnitusGain).connect(this.master);
    ring.start();

    await ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (!this.ctx || !this.master) return;
    this.master.gain.setTargetAtTime(muted ? 0 : 0.9, this.ctx.currentTime, 0.15);
  }

  update(dt: number, f: AudioFrame): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || this.muted) return;
    this.clock += dt;
    const now = ctx.currentTime;
    const T = f.active ? f.tension : 0;

    this.droneGain?.gain.setTargetAtTime(f.active ? 0.012 + T * T * 0.09 : 0.004, now, 0.4);
    this.droneFilter?.frequency.setTargetAtTime(140 + T * 520, now, 0.5);
    this.humGain?.gain.setTargetAtTime(f.hum * 0.03, now, 0.2);
    this.ambienceGain?.gain.setTargetAtTime(0.11 - T * 0.05, now, 1);

    // Heartbeat
    if (f.active && T > 0.22 && this.clock >= this.nextBeat) {
      const interval = 1.15 - T * 0.75;
      this.nextBeat = this.clock + interval;
      const amp = Math.min(1, (T - 0.22) / 0.6) * 0.55;
      this.thump(now, amp);
      this.thump(now + 0.19, amp * 0.65);
    }

    // Rain pattering on her umbrella: random drops, a few landing as fat plops.
    if (this.umbrellaRain > 0.02) {
      for (let n = this.umbrellaRain * 38 * dt; n > 0; n -= 1) {
        if (this.rng() < n) this.drop(now + this.rng() * dt, this.umbrellaRain);
      }
    }

    // Crickets fade out as tension rises.
    if (this.crickets && this.clock >= this.nextCricket) {
      this.nextCricket = this.clock + 0.4 + this.rng() * 1.8;
      if (this.rng() > T * 0.9) this.cricket(now);
    }

    // Breathing
    if (f.breath > 0.05 && this.clock >= this.nextBreath) {
      this.nextBreath = this.clock + 1.3 - f.breath * 0.6;
      this.breath(now, f.breath);
    }

    // Wind, with the odd gust.
    if (this.clock >= this.nextGust) {
      this.gustStart = this.clock;
      this.gustLength = 3 + this.rng() * 4;
      this.nextGust = this.clock + this.gustLength + 8 + this.rng() * 22;
    }
    const gt = (this.clock - this.gustStart) / this.gustLength;
    this.gust = gt >= 0 && gt <= 1 ? Math.sin(gt * Math.PI) ** 2 * (0.5 + this.windBase) : 0;
    const drift = 0.75 + 0.25 * Math.sin(this.clock * 0.21) * Math.sin(this.clock * 0.067);
    this.windGain?.gain.setTargetAtTime((this.windBase * drift * 0.05 + this.gust * 0.07) * (f.active ? 1 : 0.6), now, 0.6);
    this.windFilter?.frequency.setTargetAtTime(300 + this.gust * 520 + drift * 80, now, 0.8);

    // Tinnitus: only right at the top of the fear.
    this.tinnitusGain?.gain.setTargetAtTime(f.active && T > 0.82 ? (T - 0.82) * 0.045 : 0, now, 1.2);

    // A late train somewhere across town.
    if (this.clock >= this.nextTrain) {
      this.nextTrain = this.clock + 80 + this.rng() * 60;
      this.train(now);
    }
  }

  private src(): AudioBufferSourceNode | null {
    if (!this.ctx || !this.noise) return null;
    const s = this.ctx.createBufferSource();
    s.buffer = this.noise;
    return s;
  }

  private envGain(t: number, attack: number, peak: number, decay: number): GainNode {
    const g = this.ctx!.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    return g;
  }

  private thump(t: number, amp: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(78, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.14);
    const g = this.envGain(t, 0.012, amp, 0.16);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.25);
  }

  private cricket(t: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const o = ctx.createOscillator();
    o.frequency.value = 4300 + this.rng() * 500;
    const g = ctx.createGain();
    g.gain.value = 0;
    const pan = ctx.createStereoPanner();
    pan.pan.value = this.rng() * 2 - 1;
    const pulses = 3 + Math.floor(this.rng() * 3);
    for (let i = 0; i < pulses; i += 1) {
      const s = t + i * 0.06;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(0.012, s + 0.008);
      g.gain.linearRampToValueAtTime(0, s + 0.03);
    }
    o.connect(g).connect(pan).connect(this.master);
    o.start(t);
    o.stop(t + pulses * 0.06 + 0.05);
  }

  private breath(t: number, amount: number): void {
    const s = this.src();
    if (!s || !this.ctx || !this.master) return;
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 0.7;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.05 * amount, t + 0.25);
    g.gain.linearRampToValueAtTime(0.0, t + 0.6);
    s.connect(bp).connect(g).connect(this.master);
    s.start(t, this.rng());
    s.stop(t + 0.7);
  }

  private train(t: number, amp = 0.09, length = 9): void {
    const s = this.src();
    if (!s || !this.ctx || !this.master) return;
    s.loop = true;
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = amp > 0.2 ? 420 : 220;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(amp, t + length / 3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + length);
    s.connect(lp).connect(g).connect(this.master);
    s.start(t);
    s.stop(t + length + 0.2);
    const clacks = Math.round(length * 1.6);
    for (let i = 0; i < clacks; i += 1) {
      const ct = t + length / 6 + i * 0.42 + (i % 2) * 0.12;
      const c = this.src();
      if (!c) break;
      const bp = this.ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 500;
      const cg = this.envGain(ct, 0.005, (amp / 3) * Math.sin((i / clacks) * Math.PI) + 0.002, 0.08);
      c.connect(bp).connect(cg).connect(this.master);
      c.start(ct, this.rng());
      c.stop(ct + 0.12);
    }
  }

  // ------------------------------------------------------------ events

  /** One raindrop on the vinyl canopy: a sharp tick, sometimes a hollow plop. */
  private drop(t: number, level: number): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || !this.master) return;
    const big = this.rng() < 0.18;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = big ? 900 + this.rng() * 500 : 2600 + this.rng() * 2600;
    bp.Q.value = big ? 5 : 1.6;
    const p = ctx.createStereoPanner();
    p.pan.value = (this.rng() * 2 - 1) * 0.6;
    const amp = (big ? 0.07 : 0.035 + this.rng() * 0.03) * (0.5 + level * 0.5);
    const g = this.envGain(t, 0.001, amp, big ? 0.07 : 0.025);
    s.connect(bp).connect(g).connect(p).connect(this.master);
    s.start(t, this.rng() * 1.5);
    s.stop(t + 0.12);
  }

  /**
   * Packed snow giving underfoot: a short run of grainy crackles over a soft,
   * muffled thud. `pitch` lowers it for a heavier walker; `lp` muffles distance.
   */
  private crunch(t: number, amp: number, pitch: number, pan: number, lp: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const out = ctx.createStereoPanner();
    out.pan.value = Math.max(-1, Math.min(1, pan));
    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = lp;
    low.connect(out).connect(this.master);
    const grains = 5 + Math.floor(this.rng() * 4);
    for (let i = 0; i < grains; i += 1) {
      const s = this.src();
      if (!s) return;
      const gt = t + (i / grains) * 0.13 + this.rng() * 0.012;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = (1300 + this.rng() * 2200) * pitch;
      bp.Q.value = 1.2;
      const g = this.envGain(gt, 0.002, amp * (0.55 + this.rng() * 0.45) * (1 - (i / grains) * 0.5), 0.018 + this.rng() * 0.02);
      s.connect(bp).connect(g).connect(low);
      s.start(gt, this.rng() * 1.5);
      s.stop(gt + 0.06);
    }
    const body = this.src();
    if (!body) return;
    const bl = ctx.createBiquadFilter();
    bl.type = 'lowpass';
    bl.frequency.value = 420 * pitch;
    const bg = this.envGain(t, 0.01, amp * 0.9, 0.12);
    body.connect(bl).connect(bg).connect(low);
    body.start(t, this.rng() * 1.5);
    body.stop(t + 0.2);
  }

  footstepHer(running: boolean): void {
    const ctx = this.ctx;
    if (ctx && this.master && !this.muted && this.snow) {
      this.crunch(ctx.currentTime, running ? 0.11 : 0.085, 1, 0, 9000);
      return;
    }
    const s = this.src();
    if (!ctx || !s || !this.master || this.muted) return;
    const t = ctx.currentTime;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = running ? 1500 : 2600 + this.rng() * 300;
    bp.Q.value = running ? 0.8 : 2.2;
    const g = this.envGain(t, 0.002, running ? 0.12 : 0.1, running ? 0.09 : 0.05);
    s.connect(bp).connect(g).connect(this.master);
    s.start(t, this.rng() * 1.5);
    s.stop(t + 0.15);
  }

  footstepHim(volume: number, pan: number, running: boolean): void {
    const ctx = this.ctx;
    if (ctx && this.master && !this.muted && this.snow) {
      if (volume >= 0.01) this.crunch(ctx.currentTime, 0.2 * volume, 0.7, pan, 500 + volume * 2600);
      return;
    }
    const s = this.src();
    if (!ctx || !s || !this.master || volume < 0.01 || this.muted) return;
    const t = ctx.currentTime;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = running ? 900 : 650;
    bp.Q.value = 1.1;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 500 + volume * 2200;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    const g = this.envGain(t, 0.004, 0.22 * volume, running ? 0.1 : 0.08);
    s.connect(bp).connect(lp).connect(g).connect(p).connect(this.master);
    // Off the walls: the further he is, the more of him is echo.
    if (this.reverbIn) {
      const send = ctx.createGain();
      send.gain.value = 0.35 + (1 - volume) * 0.9;
      g.connect(send).connect(this.reverbIn);
    }
    s.start(t, this.rng() * 1.5);
    s.stop(t + 0.2);
  }

  flicker(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 118;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400;
    const g = ctx.createGain();
    g.gain.value = 0;
    let s = t;
    while (s < t + 2.6) {
      const on = 0.03 + this.rng() * 0.12;
      g.gain.setValueAtTime(0.035, s);
      g.gain.setValueAtTime(0, s + on);
      s += on + 0.04 + this.rng() * 0.2 * (1 + (s - t));
    }
    o.connect(bp).connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 2.8);
  }

  sensorClick(): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || !this.master) return;
    const t = ctx.currentTime;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2000;
    const g = this.envGain(t, 0.001, 0.25, 0.03);
    s.connect(hp).connect(g).connect(this.master);
    s.start(t);
    s.stop(t + 0.06);
    this.thump(t + 0.02, 0.3);
  }

  sting(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3000, t);
    lp.frequency.exponentialRampToValueAtTime(200, t + 3);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 3.5);
    for (const f of [110, 116.5, 155.6, 164.8, 233]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.connect(lp);
      o.start(t);
      o.stop(t + 3.6);
    }
    lp.connect(g).connect(this.master);
  }

  keys(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (let i = 0; i < 7; i += 1) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = 3200 + this.rng() * 3000;
      const st = t + i * 0.09 + this.rng() * 0.05;
      const g = this.envGain(st, 0.002, 0.03, 0.12);
      o.connect(g).connect(this.master);
      o.start(st);
      o.stop(st + 0.15);
    }
  }

  doorLock(): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || !this.master) return;
    const t = ctx.currentTime;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1200;
    const g = this.envGain(t, 0.002, 0.3, 0.08);
    s.connect(bp).connect(g).connect(this.master);
    s.start(t);
    s.stop(t + 0.12);
    this.thump(t, 0.4);
  }

  /** 0..1: rain on her open umbrella (0 when it's folded or dry). */
  /** A crow taking off: a quick run of soft, papery wingbeats. */
  wingFlap(volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted || volume < 0.01) return;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    p.connect(this.master);
    const beats = 5 + Math.floor(this.rng() * 3);
    for (let i = 0; i < beats; i += 1) {
      const s = this.src();
      if (!s) return;
      // Wingbeats slow down as it climbs away.
      const t = ctx.currentTime + i * (0.075 + i * 0.012);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 650 + this.rng() * 500;
      bp.Q.value = 0.9;
      const g = this.envGain(t, 0.012, volume * 0.16 * (1 - i / (beats + 2)), 0.06);
      s.connect(bp).connect(g).connect(p);
      s.start(t, this.rng() * 1.5);
      s.stop(t + 0.1);
    }
  }

  /** A crow's harsh "kaa": two or three rasping calls, falling in pitch. */
  caw(volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted || volume < 0.01) return;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    // Distant calls lose their top end.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400 + volume * 3000;
    lp.connect(p).connect(this.master);
    const calls = 2 + Math.floor(this.rng() * 2);
    const base = 520 + this.rng() * 120;
    for (let i = 0; i < calls; i += 1) {
      const t = ctx.currentTime + i * (0.34 + this.rng() * 0.08);
      const len = 0.22 + this.rng() * 0.06;
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(base * 1.08, t);
      o.frequency.exponentialRampToValueAtTime(base * 0.82, t + len);
      // Rasp: a fast wobble on the pitch.
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 55 + this.rng() * 20;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = base * 0.06;
      lfo.connect(lfoGain).connect(o.frequency);
      // Two formants give it the throaty "aa".
      const f1 = ctx.createBiquadFilter();
      f1.type = 'bandpass';
      f1.frequency.value = 1100;
      f1.Q.value = 3;
      const f2 = ctx.createBiquadFilter();
      f2.type = 'bandpass';
      f2.frequency.value = 2300;
      f2.Q.value = 4;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(volume * 0.22, t + 0.03);
      g.gain.setValueAtTime(volume * 0.2, t + len * 0.7);
      g.gain.exponentialRampToValueAtTime(0.0001, t + len);
      o.connect(f1).connect(g);
      o.connect(f2).connect(g);
      g.connect(lp);
      o.start(t);
      lfo.start(t);
      o.stop(t + len + 0.02);
      lfo.stop(t + len + 0.02);
    }
  }

  setUmbrellaRain(level: number): void {
    this.umbrellaRain = level;
  }

  /** Snow underfoot: footsteps crunch. */
  setSnow(snow: boolean): void {
    this.snow = snow;
  }

  /** Daytime: no crickets. */
  setDaytime(day: boolean): void {
    this.crickets = !day;
  }

  /** 0..1 rain loudness (fades under shelter). */
  setRain(level: number): void {
    if (!this.ctx || !this.rainGain) return;
    this.rainGain.gain.setTargetAtTime(level * 0.09, this.ctx.currentTime, 0.4);
  }

  /** A shop shutter rattling down. */
  shutter(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (let i = 0; i < 16; i += 1) {
      const s = this.src();
      if (!s) break;
      const st = t + i * 0.07;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 600 + this.rng() * 500;
      const g = this.envGain(st, 0.003, 0.05 + (i === 15 ? 0.15 : 0), 0.06);
      s.connect(bp).connect(g).connect(this.master);
      s.start(st, this.rng());
      s.stop(st + 0.1);
    }
  }

  /** Phone ringing (unknown caller). */
  ring(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (let k = 0; k < 3; k += 1) {
      for (const [f, d] of [[880, 0], [1100, 0.12]] as const) {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.value = f;
        const st = t + k * 1.1 + d;
        const g = this.envGain(st, 0.01, 0.05, 0.25);
        o.connect(g).connect(this.master);
        o.start(st);
        o.stop(st + 0.3);
      }
    }
  }

  /** Her screen waking (a soft tick and a brief glassy blip) or locking (just the tick). */
  phoneTap(waking: boolean): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || !this.master) return;
    const t = ctx.currentTime;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 4000;
    const g = this.envGain(t, 0.001, 0.03, 0.015);
    s.connect(hp).connect(g).connect(this.master);
    s.start(t, this.rng());
    s.stop(t + 0.03);
    if (!waking) return;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(1900, t + 0.02);
    o.frequency.exponentialRampToValueAtTime(2600, t + 0.08);
    const og = this.envGain(t + 0.02, 0.004, 0.018, 0.09);
    o.connect(og).connect(this.master);
    o.start(t + 0.02);
    o.stop(t + 0.14);
  }

  /** Flashlight switch. */
  click(): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || !this.master) return;
    const t = ctx.currentTime;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 3000;
    const g = this.envGain(t, 0.001, 0.05, 0.02);
    s.connect(hp).connect(g).connect(this.master);
    s.start(t, this.rng());
    s.stop(t + 0.04);
  }

  /** A train going right past her: loud rumble and wheel clacks. */
  trainPass(seconds = 9): void {
    if (!this.ctx) return;
    this.train(this.ctx.currentTime, 0.32, seconds);
  }

  /** Level-crossing bell: one "kan" (call it about twice a second). */
  crossingBell(volume: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || volume < 0.01 || this.muted) return;
    const t = ctx.currentTime;
    for (const [f, a] of [[1180, 1], [2370, 0.35], [3510, 0.15]] as const) {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = this.envGain(t, 0.002, 0.09 * volume * a, 0.32);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.4);
    }
  }

  /** The whole district losing power: a deep thunk and the hum dying away. */
  blackout(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(30, t + 1.6);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 300;
    const g = this.envGain(t, 0.01, 0.12, 1.6);
    o.connect(lp).connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 1.8);
    this.thump(t, 0.5);
  }

  /** The phone dying: a falling tone, then nothing. */
  powerDown(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, t);
    o.frequency.exponentialRampToValueAtTime(110, t + 0.35);
    const g = this.envGain(t, 0.01, 0.07, 0.4);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.45);
  }

  /** Incoming text: the soft two-note chime of her phone. */
  textPing(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    [1318.5, 1760].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      const st = t + i * 0.13;
      const g = this.envGain(st, 0.005, 0.06, 0.35);
      o.connect(g).connect(this.master!);
      o.start(st);
      o.stop(st + 0.45);
    });
  }

  meow(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(620, t);
    o.frequency.linearRampToValueAtTime(980, t + 0.18);
    o.frequency.linearRampToValueAtTime(560, t + 0.5);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1500;
    bp.Q.value = 2;
    const g = this.envGain(t, 0.05, 0.05, 0.45);
    o.connect(bp).connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.6);
  }

  /** A dog's bark: a short, rough, falling yelp, panned toward the dog. */
  bark(volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted) return;
    const t = ctx.currentTime;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    p.connect(this.master);
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    const f0 = 380 + this.rng() * 120;
    o.frequency.setValueAtTime(f0 * 0.8, t);
    o.frequency.linearRampToValueAtTime(f0 * 1.3, t + 0.03);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.55, t + 0.16);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 1.4;
    const g = this.envGain(t, 0.008, 0.3 * volume, 0.16);
    o.connect(bp).connect(g).connect(p);
    o.start(t);
    o.stop(t + 0.22);
    // Breath noise for grit.
    const s = this.src();
    if (s) {
      const hp = ctx.createBiquadFilter();
      hp.type = 'bandpass';
      hp.frequency.value = 1600;
      const ng = this.envGain(t, 0.005, 0.12 * volume, 0.12);
      s.connect(hp).connect(ng).connect(p);
      s.start(t, this.rng() * 1.5);
      s.stop(t + 0.2);
    }
  }

  /** Low growl that rises before the barking starts. */
  growl(volume: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(70, t);
    o.frequency.linearRampToValueAtTime(95, t + 0.5);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 23;
    const lg = ctx.createGain();
    lg.gain.value = 12;
    lfo.connect(lg).connect(o.frequency);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 420;
    const g = this.envGain(t, 0.2, 0.22 * volume, 0.45);
    o.connect(lp).connect(g).connect(this.master);
    o.start(t);
    lfo.start(t);
    o.stop(t + 0.7);
    lfo.stop(t + 0.7);
  }

  /** Shoulders colliding: a dull thud and a rustle of coats. */
  bump(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.muted) return;
    const t = ctx.currentTime;
    this.thump(t, 0.5);
    const s = this.src();
    if (!s) return;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1100;
    bp.Q.value = 0.7;
    const g = this.envGain(t, 0.01, 0.14, 0.25);
    s.connect(bp).connect(g).connect(this.master);
    s.start(t, this.rng() * 1.5);
    s.stop(t + 0.3);
  }

  // ------------------------------------------------------------ unease

  /** How windy the night is (0 still .. 1 blustery). */
  setWind(level: number): void {
    this.windBase = Math.max(0, Math.min(1, level));
  }

  /** A synthetic room tail: decaying noise with a few early reflections, slightly different per ear. */
  private impulse(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch += 1) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i += 1) {
        const t = i / ctx.sampleRate;
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6) * (t < 0.012 ? t / 0.012 : 1);
      }
      // Slap-back off the house fronts.
      for (const [at, amp] of [[0.043, 0.5], [0.071, 0.35], [0.112, 0.25]]) {
        const i = Math.floor((at + ch * 0.006) * ctx.sampleRate);
        if (i < len) d[i] += amp * (ch ? -1 : 1);
      }
    }
    return buf;
  }

  /** Output for a placed sound: muffled by `lp`, panned, with `wet` sent into the night air. */
  private bus(t: number, pan: number, wet: number, lp = 18000): AudioNode | null {
    const ctx = this.ctx;
    if (!ctx || !this.master) return null;
    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = lp;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    low.connect(p).connect(this.master);
    if (this.reverbIn && wet > 0) {
      const send = ctx.createGain();
      send.gain.value = wet;
      low.connect(send).connect(this.reverbIn);
    }
    // Let the graph go once the sound (and its tail) is done.
    window.setTimeout(() => low.disconnect(), (t - ctx.currentTime + 8) * 1000);
    return low;
  }

  /**
   * One footstep that shouldn't be there: a heel on asphalt somewhere behind
   * her, mostly echo. (He stopped a beat after she did. Or nobody is there.)
   */
  phantomStep(volume: number, pan: number, delay = 0): void {
    const ctx = this.ctx;
    const s = this.src();
    if (!ctx || !s || this.muted || volume < 0.01) return;
    const t = ctx.currentTime + delay;
    const out = this.bus(t, pan, 1.3, 600 + volume * 1800);
    if (!out) return;
    if (this.snow) {
      this.crunch(t, 0.16 * volume, 0.7, pan, 500 + volume * 1800);
      return;
    }
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 650;
    bp.Q.value = 1.1;
    const g = this.envGain(t, 0.004, 0.22 * volume, 0.08);
    s.connect(bp).connect(g).connect(out);
    s.start(t, this.rng() * 1.5);
    s.stop(t + 0.2);
  }

  /** Breath and half a word, close behind her. */
  whisper(pan: number, volume = 1): void {
    const ctx = this.ctx;
    if (!ctx || this.muted) return;
    const t = ctx.currentTime;
    const out = this.bus(t, pan, 0.25, 4200);
    if (!out) return;
    // Two breathy syllables: a hiss that opens into a vowel, then a sigh.
    const parts: [number, number, number, number][] = [
      [0, 0.55, 2600, 900],
      [0.62, 0.9, 1300, 650],
    ];
    for (const [at, len, f0, f1] of parts) {
      const s = this.src();
      if (!s) return;
      const st = t + at;
      for (const [mul, q] of [[1, 6], [2.3, 9]] as const) {
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.Q.value = q;
        bp.frequency.setValueAtTime(f0 * mul, st);
        bp.frequency.exponentialRampToValueAtTime(f1 * mul, st + len);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, st);
        g.gain.exponentialRampToValueAtTime(0.11 * volume / mul, st + len * 0.3);
        g.gain.exponentialRampToValueAtTime(0.0001, st + len);
        s.connect(bp).connect(g).connect(out);
      }
      s.start(st, this.rng() * 1.5);
      s.stop(st + len + 0.05);
    }
  }

  /** Dry bamboo knocking together in the wind: a few hollow tocks. */
  bambooKnock(volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || volume < 0.01) return;
    const t0 = ctx.currentTime;
    const out = this.bus(t0, pan, 0.5);
    if (!out) return;
    const n = 2 + Math.floor(this.rng() * 4);
    let t = t0;
    for (let i = 0; i < n; i += 1) {
      const s = this.src();
      if (!s) return;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 480 + this.rng() * 520;
      bp.Q.value = 14;
      const g = this.envGain(t, 0.001, 0.5 * volume * (0.6 + this.rng() * 0.4), 0.09);
      s.connect(bp).connect(g).connect(out);
      s.start(t, this.rng() * 1.5);
      s.stop(t + 0.15);
      t += 0.07 + this.rng() * 0.22;
    }
    // Leaves hissing as the gust goes through.
    const r = this.src();
    if (!r) return;
    const hp = ctx.createBiquadFilter();
    hp.type = 'bandpass';
    hp.frequency.value = 3400;
    hp.Q.value = 0.6;
    const rg = ctx.createGain();
    rg.gain.setValueAtTime(0.0001, t0);
    rg.gain.exponentialRampToValueAtTime(0.03 * volume, t0 + 0.5);
    rg.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.8);
    r.connect(hp).connect(rg).connect(out);
    r.start(t0, this.rng() * 1.5);
    r.stop(t0 + 1.9);
  }

  /** A streetlight stuttering: the buzz and tick of a failing ballast. */
  lampZap(volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || volume < 0.01) return;
    const t = ctx.currentTime;
    const out = this.bus(t, pan, 0.2);
    if (!out) return;
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 100;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1700;
    bp.Q.value = 1.5;
    const g = ctx.createGain();
    g.gain.value = 0;
    let st = t;
    while (st < t + 0.8) {
      const on = 0.02 + this.rng() * 0.07;
      g.gain.setValueAtTime(0.06 * volume, st);
      g.gain.setValueAtTime(0, st + on);
      st += on + 0.03 + this.rng() * 0.12;
    }
    o.connect(bp).connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.9);
  }

  /**
   * The far-off sounds' output: dull and flattened, like they come from
   * blocks away through walls. Lightly saturated (warm, not fuzzy),
   * compressed so nothing pokes out, muffled, with the faintest slow sag in
   * pitch. Then the usual pan, distance filter and night reverb.
   */
  private warpBus(t: number, pan: number, wet: number, lp: number, seconds: number): AudioNode | null {
    const ctx = this.ctx;
    const out = this.bus(t, pan, wet, lp);
    if (!ctx || !out) return null;
    const end = t + seconds + 0.5;
    const drive = ctx.createGain();
    drive.gain.value = 3;
    const sat = ctx.createWaveShaper();
    sat.curve = (this.clipCurve ??= shaperCurve((x) => Math.tanh(x * 1.6) / Math.tanh(1.6)));
    sat.oversample = '4x';
    const post = ctx.createGain();
    post.gain.value = 0.28;
    // Flatten it: squash the peaks so it's a dull, even shape of a sound.
    const flat = ctx.createDynamicsCompressor();
    flat.threshold.value = -30;
    flat.knee.value = 12;
    flat.ratio.value = 6;
    flat.attack.value = 0.01;
    flat.release.value = 0.3;
    // Muffled: the highs go, the low body stays.
    const muffle = ctx.createBiquadFilter();
    muffle.type = 'lowpass';
    muffle.frequency.value = 700;
    muffle.Q.value = 0.4;
    const body = ctx.createBiquadFilter();
    body.type = 'lowshelf';
    body.frequency.value = 220;
    body.gain.value = 5;
    // A very slow, slight sag in pitch: wrong without being wobbly.
    const sag = ctx.createDelay(0.1);
    sag.delayTime.value = 0.02;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.12 + this.rng() * 0.1;
    const lfoAmt = ctx.createGain();
    lfoAmt.gain.value = 0.004;
    lfo.connect(lfoAmt).connect(sag.delayTime);
    lfo.start(t);
    lfo.stop(end);
    drive.connect(sat).connect(post).connect(flat).connect(muffle).connect(body).connect(sag).connect(out);
    // Off tune: a second copy, slightly late, sliding steadily flat against the first
    // (a delay that keeps lengthening lowers the pitch), so the two sour and beat.
    const sour = ctx.createDelay(4);
    const slide = DETUNE_SLIDE * (0.7 + this.rng() * 0.6);
    sour.delayTime.setValueAtTime(0.012, t);
    sour.delayTime.linearRampToValueAtTime(Math.min(3.9, 0.012 + seconds * slide), t + seconds);
    const sourGain = ctx.createGain();
    sourGain.gain.value = 0.75;
    body.connect(sour).connect(sourGain).connect(out);
    window.setTimeout(() => drive.disconnect(), (end - ctx.currentTime + 8) * 1000);
    return drive;
  }

  /** Something far off across the neighbourhood, at `volume` (0..1) from `pan`. */
  distant(kind: DistantSound, volume: number, pan: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || volume < 0.01) return;
    const t = ctx.currentTime;
    switch (kind) {
      case 'chime':
        this.chime(t, volume, pan);
        break;
      case 'howl':
        this.howl(t, volume, pan);
        break;
      case 'clang': {
        const out = this.warpBus(t, pan, 1.6, 2600, 2.8);
        if (!out) return;
        for (const [f, amp, len] of [[107, 0.06, 3.6], [260, 0.035, 2.5], [451, 0.02, 1.7], [665, 0.01, 1]]) {
          const o = ctx.createOscillator();
          o.frequency.value = f * (0.98 + this.rng() * 0.04);
          const g = this.envGain(t, 0.002, amp * volume, len);
          o.connect(g).connect(out);
          o.start(t);
          o.stop(t + len + 0.1);
        }
        break;
      }
      case 'slam': {
        const out = this.warpBus(t, pan, 1.8, 1400, 0.5);
        const s = this.src();
        if (!out || !s) return;
        const g = this.envGain(t, 0.003, 0.35 * volume, 0.25);
        s.connect(g).connect(out);
        s.start(t, this.rng() * 1.5);
        s.stop(t + 0.35);
        const o = ctx.createOscillator();
        o.frequency.setValueAtTime(42, t);
        o.frequency.exponentialRampToValueAtTime(24, t + 0.3);
        const og = this.envGain(t, 0.005, 0.3 * volume, 0.3);
        o.connect(og).connect(out);
        o.start(t);
        o.stop(t + 0.4);
        break;
      }
      case 'cry': {
        // A cat yowling somewhere, or something that sounds like one.
        const out = this.warpBus(t, pan, 1.4, 2600, 3.1);
        if (!out) return;
        for (const [at, len] of [[0, 1.5], [1.9, 1.1]]) {
          const st = t + at;
          const o = ctx.createOscillator();
          o.type = 'sawtooth';
          o.frequency.setValueAtTime(250, st);
          o.frequency.linearRampToValueAtTime(375, st + len * 0.45);
          o.frequency.linearRampToValueAtTime(210, st + len);
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 480;
          bp.Q.value = 1.6;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.0001, st);
          g.gain.exponentialRampToValueAtTime(0.045 * volume, st + len * 0.3);
          g.gain.exponentialRampToValueAtTime(0.0001, st + len);
          o.connect(bp).connect(g).connect(out);
          // A second voice just under it, bending the other way: sour, not quite a cat.
          const o2 = ctx.createOscillator();
          o2.type = 'sawtooth';
          o2.frequency.setValueAtTime(250 * 0.94, st);
          o2.frequency.linearRampToValueAtTime(375 * 0.9, st + len * 0.55);
          o2.frequency.linearRampToValueAtTime(210 * 0.97, st + len);
          const g2 = ctx.createGain();
          g2.gain.value = 0.6;
          o2.connect(g2).connect(bp);
          o.start(st);
          o2.start(st);
          o.stop(st + len + 0.05);
          o2.stop(st + len + 0.05);
        }
        break;
      }
    }
  }

  /**
   * The ward's evening chime (the "Yuyake Koyake" tune from a loudspeaker
   * pole), far away and wrong: late, warbling, sagging flat as it plays.
   */
  private chime(t: number, volume: number, pan: number): void {
    const ctx = this.ctx;
    const out = this.warpBus(t, pan, 1.7, 2600, 9);
    if (!ctx || !out) return;
    const G = 196;
    const notes: [number, number][] = [
      [G, 0.5], [G, 0.5], [G * 1.122, 0.5], [G, 0.5], [G * 0.841, 0.5], [G * 0.841, 0.5], [G * 0.749, 1],
      [G * 0.841, 0.5], [G * 0.749, 0.5], [G * 0.667, 0.5], [G * 0.749, 0.5], [G * 0.841, 1.5],
    ];
    let st = t;
    notes.forEach(([f, len], i) => {
      // Sags a whole semitone over the phrase, every note a little wrong (up to ~40 cents
      // either way), and each one droops as it rings.
      const sag = Math.pow(2, (-1 * i) / notes.length / 12);
      const wrong = Math.pow(2, ((this.rng() - 0.5) * 0.8) / 12);
      for (const [mul, amp] of [[1, 1], [2.76, 0.25], [5.4, 0.08]]) {
        const o = ctx.createOscillator();
        o.frequency.setValueAtTime(f * mul * sag * wrong, st);
        o.frequency.linearRampToValueAtTime(f * mul * sag * wrong * 0.975, st + len);
        const g = this.envGain(st, 0.01, 0.05 * volume * amp, len * 1.6);
        o.connect(g).connect(out);
        o.start(st);
        o.stop(st + len * 1.7);
      }
      st += len * (1 + (this.rng() - 0.5) * 0.08);
    });
  }

  /** A dog howling a long way off. */
  private howl(t: number, volume: number, pan: number): void {
    const ctx = this.ctx;
    const out = this.warpBus(t, pan, 1.6, 2000, 3.8);
    if (!ctx || !out) return;
    const len = 3.6;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(165, t);
    o.frequency.linearRampToValueAtTime(277, t + 0.9);
    o.frequency.linearRampToValueAtTime(259, t + 2.4);
    o.frequency.linearRampToValueAtTime(146, t + len);
    const vib = ctx.createOscillator();
    vib.frequency.value = 5.2;
    const vibAmt = ctx.createGain();
    vibAmt.gain.value = 9;
    vib.connect(vibAmt).connect(o.frequency);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 390;
    bp.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05 * volume, t + 0.8);
    g.gain.setValueAtTime(0.05 * volume, t + 2.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    o.connect(bp).connect(g).connect(out);
    // A second throat a semitone-and-a-bit above, out of step with the first: it doesn't sound like one dog.
    const o2 = ctx.createOscillator();
    o2.type = 'sawtooth';
    o2.frequency.setValueAtTime(165 * 1.07, t + 0.15);
    o2.frequency.linearRampToValueAtTime(277 * 1.07, t + 1.1);
    o2.frequency.linearRampToValueAtTime(259 * 1.04, t + 2.5);
    o2.frequency.linearRampToValueAtTime(146 * 0.97, t + len);
    vib.connect(vibAmt).connect(o2.frequency);
    const g2 = ctx.createGain();
    g2.gain.value = 0.55;
    o2.connect(g2).connect(bp);
    o.start(t);
    o2.start(t);
    vib.start(t);
    o.stop(t + len + 0.1);
    o2.stop(t + len + 0.1);
    vib.stop(t + len + 0.1);
  }

  /** Video call ringing: a rising marimba figure, over and over, for `seconds`. */
  callRing(seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const notes = [659.3, 830.6, 987.8, 1318.5];
    for (let k = 0; k * 1.4 < seconds; k += 1) {
      notes.forEach((f, i) => {
        const st = t + k * 1.4 + i * 0.11;
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = f;
        const g = this.envGain(st, 0.004, 0.07, 0.22);
        o.connect(g).connect(this.master!);
        o.start(st);
        o.stop(st + 0.3);
      });
    }
  }

  /** Digital crackle: the call connecting, or the map breaking up. */
  glitch(seconds: number, volume = 1): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    let at = t;
    while (at < t + seconds) {
      const s = this.src();
      if (!s) return;
      const len = 0.015 + this.rng() * 0.06;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 800 + this.rng() * 5000;
      bp.Q.value = 0.8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.setValueAtTime((0.05 + this.rng() * 0.09) * volume, at + 0.002);
      g.gain.setValueAtTime(0.0001, at + len);
      s.connect(bp).connect(g).connect(this.master);
      s.start(at, this.rng() * 1.5);
      s.stop(at + len + 0.01);
      // A buzz of square tone now and then (data on the line).
      if (this.rng() < 0.3) {
        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.value = 180 + this.rng() * 900;
        const og = ctx.createGain();
        og.gain.setValueAtTime(0.0001, at);
        og.gain.setValueAtTime(0.02 * volume, at + 0.002);
        og.gain.setValueAtTime(0.0001, at + len);
        o.connect(og).connect(this.master);
        o.start(at);
        o.stop(at + len + 0.01);
      }
      at += len + this.rng() * 0.05;
    }
  }

  /** His face: a slammed, screeching, dissonant hit. `big` for the call, small for the map flash. */
  jumpScare(big = true): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const amp = big ? 1 : 0.55;
    const len = big ? 1.4 : 0.6;
    // A burst of noise, loud and short.
    const s = this.src();
    if (s) {
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 600;
      const g = this.envGain(t, 0.004, 0.32 * amp, 0.35);
      s.connect(hp).connect(g).connect(this.master);
      s.start(t, this.rng());
      s.stop(t + 0.5);
    }
    // A cluster of detuned saws, bending down.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(6000, t);
    lp.frequency.exponentialRampToValueAtTime(400, t + len);
    const g = this.envGain(t, 0.006, 0.2 * amp, len);
    for (const f of [311, 329.6, 466.2, 493.9, 698.5]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.setValueAtTime(f, t);
      o.frequency.exponentialRampToValueAtTime(f * 0.82, t + len);
      o.connect(lp);
      o.start(t);
      o.stop(t + len + 0.1);
    }
    lp.connect(g).connect(this.master);
    // A screech on top.
    const sc = ctx.createOscillator();
    sc.type = 'square';
    sc.frequency.setValueAtTime(2400, t);
    sc.frequency.linearRampToValueAtTime(3100, t + len * 0.6);
    const sbp = ctx.createBiquadFilter();
    sbp.type = 'bandpass';
    sbp.frequency.value = 2800;
    const sg = this.envGain(t, 0.01, 0.05 * amp, len * 0.7);
    sc.connect(sbp).connect(sg).connect(this.master);
    sc.start(t);
    sc.stop(t + len);
    this.thump(t, 0.6 * amp);
    if (big) this.thump(t + 0.09, 0.4);
  }

  dispose(): void {
    void this.ctx?.close();
    this.ctx = null;
  }
}

/** How fast the far-off sounds' second copy slides flat (0.03 = 3%, about half a semitone). */
const DETUNE_SLIDE = 0.03;

/** A WaveShaper curve sampling `f` over -1..1. */
function shaperCurve(f: (x: number) => number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i += 1) curve[i] = f((i / (n - 1)) * 2 - 1);
  return curve;
}
